<template>
  <div
    ref="fab"
    :class="$b({ isOpen, defaultExpanded })"
    :style="dimensionVars"
  >
    <div
      ref="options"
      :class="$b('options')"
    >
      <button
        v-for="(option, idx) in options"
        :key="option.name"
        :class="$b('option-item', { active: activeOptionIndex === idx })"
        @click="$emit('click-option', { idx: idx, value: option.value })"
      >
        <img
          v-if="option.image"
          :src="option.image"
          :alt="option.name"
          :class="$b('option-image')"
        />
        <div :class="$b('option-overlay')" />
        <VisionIcon
          v-if="activeOptionIndex === idx"
          icon="scene"
          colour="white"
          :class="$b('active-icon')"
        />
      </button>
    </div>
    <button
      v-if="!defaultExpanded"
      :class="$b('button')"
      @click="isOpen = !isOpen"
    >
      <VisionIcon
        icon="scene"
        colour="black"
        :class="$b('button-icon-open')"
      />
      <VisionIcon
        icon="cross"
        colour="black"
        :class="$b('button-icon-close')"
      />
    </button>
  </div>
</template>

<script>
import { debounce } from '~/assets/js/helpers'

export default {
  name: 'FloatingActionButton',
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    activeOptionIndex: { type: Number, default: 0 },
    defaultExpanded: { type: Boolean, default: false },
  },
  emits: ['click-option'],
  data() {
    return {
      isOpen: false,
      desktopClosed: 'auto',
      desktopOpen: 'auto',
      mobileClosed: 'auto',
      mobileOpen: 'auto',
    }
  },
  computed: {
    dimensionVars() {
      return {
        '--desktopClosed': this.desktopClosed,
        '--desktopOpen': this.desktopOpen,
        '--mobileClosed': this.mobileClosed,
        '--mobileOpen': this.mobileOpen
      }
    },
  },
  mounted() {
    this.getDimensions = debounce(this.getDimensions, 500)
    window.addEventListener('resize', this.getDimensions)
    this.getDimensions()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.getDimensions)
  },
  methods: {
    getDimensions() {
      if (!import.meta.client) return

      this.$nextTick(() => {
        const elem = this.$refs.fab
        const childElem = this.$refs.options

        // Reset state
        elem.classList.remove('FloatingActionButton--is-open')
        elem.style.height = 'auto'
        elem.style.width = 'auto'

        this.desktopClosed = getComputedStyle(elem).width
        this.mobileClosed = getComputedStyle(elem).height

        elem.classList.add('FloatingActionButton--is-open')

        elem.style.display = 'flex'
        elem.style.position = 'absolute'
        elem.style.visibility = 'hidden'
        elem.style.height = 'auto'
        elem.style.width = 'auto'
        elem.style.paddingRight = '0'

        childElem.style.display = 'grid'

        this.desktopOpen = getComputedStyle(elem).width
        this.mobileOpen = getComputedStyle(elem).height

        elem.style.width = null
        elem.style.position = null
        elem.style.visibility = null
        elem.style.paddingBottom = null
        elem.style.paddingRight = null

        elem.classList.remove('FloatingActionButton--is-open')

        childElem.style.display = null

        // Force repaint to make sure the
        // animation is triggered correctly.
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        getComputedStyle(elem).height

        // Trigger the animation.
        // We use `requestAnimationFrame` because we need
        // to make sure the browser has finished
        // painting after setting the `height`
        // to `0` in the line above.
        requestAnimationFrame(() => {
          elem.style.height = null
          elem.style.width = null
        })
      })
    },
  },
}
</script>

<style lang="postcss">
.FloatingActionButton {
  @apply bg-grey-light
    flex flex-col md:flex-row justify-end md:justify-center
    overflow-hidden;
  padding: 4px;
  border-radius: 8px;
  height: var(--mobileClosed);
  transition: height 0.1s ease-in;

  @screen md {
    height: auto;
    width: auto;
  }

  &--is-open {
    @apply md:pr-0;
    height: var(--mobileOpen);

    @screen md {
      width: auto;
      height: auto;
    }
  }

  &--default-expanded {
    @apply flex-row;
  }
}

.FloatingActionButton__button {
  @apply flex md:hidden items-center justify-center flex-shrink-0;
  width: 48px;
  height: 48px;
  font-size: 14px;

  @screen md {
    @apply px-sm;
    height: 52px;
    width: auto;
  }
}

.FloatingActionButton__button-icon-open {
  @apply opacity-100 block;
  transition: opacity 0s 0.1s, transform 0.2s ease-in-out;

  .FloatingActionButton--is-open & {
    @apply opacity-0 hidden;
    transition: opacity 0.1s 0.1s;
  }

  .FloatingActionButton__button:hover & {
    @screen lg {
      --icon-fill: theme('colors.purple') !important;
      transform: scale(1.2);
    }
  }
}

.FloatingActionButton__button .FloatingActionButton__button-icon-close {
  @apply opacity-0 hidden;
  transition: opacity 0.1s 0.1s, transform 0.2s ease-in-out;

  .FloatingActionButton--is-open & {
    @apply opacity-100 block;
    transition: opacity 0s 0.1s;
  }

  .FloatingActionButton__button:hover & {
    @screen lg {
      --icon-stroke: theme('colors.purple') !important;
      transform: scale(1.2);
    }
  }
}

.FloatingActionButton__options {
  @apply hidden md:grid grid-cols-1 md:grid-cols-3;

  grid-gap: 4px;

  @screen mdMax {
    padding-bottom: 4px;
  }

  .FloatingActionButton--is-open & {
    @apply grid;
  }

  .FloatingActionButton--default-expanded & {
    @apply grid grid-cols-3 pb-0;
  }
}

.FloatingActionButton__option-item {
  @apply overflow-hidden relative flex items-center justify-center;
  height: 48px;
  width: 48px;
  border-radius: 4px;

  @screen md {
    height: 52px;
    width: 52px;
  }
}

.FloatingActionButton__option-overlay {
  @apply absolute h-full w-full inset-0 opacity-0;
  background-image: var(--gradient);

  .FloatingActionButton__option-item--active & {
    @apply opacity-70;
  }

  .FloatingActionButton__option-item:hover & {
    @apply lg:opacity-40;
  }
}

.FloatingActionButton__option-image {
  @apply object-cover
    h-full w-full;

  object-position: center;
}

.FloatingActionButton__active-icon {
  @apply absolute;
}
</style>
